import React, { useState, useEffect } from 'react';
import {  FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaUsers, FaSortAmountDown, FaSortAmountUp, FaPhoneAlt, FaIdCard } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);


export default function TercerosTable() {
  const [terceros, setTerceros] = useState([]);
  const [filteredTerceros, setFilteredTerceros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchTerceros();
  }, []);

  useEffect(() => {
    filterAndSortTerceros();
  }, [searchTerm, terceros, sortColumn, sortDirection, filterType]);

  const fetchTerceros = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/terceros');
      setTerceros(response.data);
      setFilteredTerceros(response.data);
    } catch (err) {
      setError('Error al cargar los terceros. Por favor, intente de nuevo.');
      console.error('Error fetching terceros:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortTerceros = () => {
    let filtered = terceros.filter(tercero =>
      tercero.documento.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tercero.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filterType) {
      filtered = filtered.filter(tercero => tercero.tipoDocumento === filterType);
    }

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredTerceros(sorted);
    setCurrentPage(1);
  };

  const handleDeleteTercero = async (documento) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tercero?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/terceros/${documento}`);
        await fetchTerceros();
      } catch (err) {
        setError('Error al eliminar el tercero. Por favor, intente de nuevo.');
        console.error('Error deleting tercero:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTerceros.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const tipoDocumentoData = terceros.reduce((acc, tercero) => {
    acc[tercero.tipoDocumento] = (acc[tercero.tipoDocumento] || 0) + 1;
    return acc;
  }, {});

  const pieChartData = Object.entries(tipoDocumentoData).map(([name, value]) => ({ name, value }));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Terceros</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard title="Total Terceros" value={terceros.length} icon={<FaUsers />} color="from-blue-500 to-blue-700" />
        <StatCard title="Tipos de Documento" value={Object.keys(tipoDocumentoData).length} icon={<FaIdCard/>} color="from-green-500 to-green-700" />
        <StatCard title="Contactos con Teléfono" value={terceros.filter(t => t.telefono).length} icon={<FaPhoneAlt />} color="from-yellow-500 to-orange-600" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Terceros</h2>
          <Link
            to="/home/terceros/add"
            className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-900 transition-colors duration-200 flex items-center"
          >
            <FaPlus className="mr-2" /> Añadir Tercero
          </Link>
        </div>
        <div className="p-4 flex flex-wrap justify-between items-center bg-gray-100">
          <div className="flex items-center mb-2 md:mb-0">
            <FaSearch className="text-blue-700 mr-2" />
            <input
              type="text"
              placeholder="Buscar por Documento o Nombre"
              className="border p-2 rounded w-72"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-4">
            <div>
              <label htmlFor="filterType" className="mr-2">Filtrar por Tipo:</label>
              <select
                id="filterType"
                className="border p-2 rounded"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">Todos</option>
                {Object.keys(tipoDocumentoData).map(tipo => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="itemsPerPage" className="mr-2">Mostrar:</label>
              <select
                id="itemsPerPage"
                className="border p-2 rounded"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={filteredTerceros.length}>Todos</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('tipoDocumento')}>
                  Tipo Documento {sortColumn === 'tipoDocumento' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('documento')}>
                  Documento {sortColumn === 'documento' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('nombre')}>
                  Nombre {sortColumn === 'nombre' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('telefono')}>
                  Teléfono {sortColumn === 'telefono' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map(tercero => (
                <tr key={tercero.documento} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{tercero.tipoDocumento}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{tercero.documento}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{tercero.nombre}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{tercero.telefono}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <Link to={`/home/terceros/${tercero.documento}`} className="text-blue-600 hover:text-blue-900">
                        <FaEye className="h-5 w-5" />
                      </Link>
                      <Link to={`/home/terceros/edit/${tercero.documento}`} className="text-green-600 hover:text-green-900">
                        <FaEdit className="h-5 w-5" />
                      </Link>
                      <button onClick={() => handleDeleteTercero(tercero.documento)} className="text-red-600 hover:text-red-900">
                        <FaTrash className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 flex justify-between items-center bg-gray-50">
          <div>
            Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredTerceros.length)} de {filteredTerceros.length} terceros
          </div>
          <div>
            {Array.from({ length: Math.ceil(filteredTerceros.length / itemsPerPage) }, (_, i) => (
              <button
                key={i}
                onClick={() => paginate(i + 1)}
                className={`mx-1 px-3 py-1 rounded ${currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}